import { Info, X } from "lucide-react";
import { useGetProfile, useUpdateProfile, getGetProfileQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Phase 2 (Task #56) — small inline reminder shown next to free-text
 * inputs that may attract sensitive personal information. The wording
 * is intentionally cautious: it nudges teachers toward concise,
 * professional notes without making any compliance/anonymity claim.
 *
 * Task #79 — the tip is now globally dismissible. Once the teacher
 * clicks the X on any surface, the dismissal is persisted to
 * `profiles.dismissedFreeTextWarning` and the tip stays hidden across
 * every surface (student notes, record form, diary editor, notes page,
 * AI lesson-prep drawer). This keeps the helpful first-time nudge
 * without nagging experienced users who already know the data
 * minimisation posture.
 */
export function DataMinimisationTip({
  className = "",
  text,
}: {
  className?: string;
  /** Override the default tip text for context-specific surfaces. */
  text?: string;
}) {
  const { data: profile } = useGetProfile();
  const updateProfile = useUpdateProfile();
  const queryClient = useQueryClient();

  // While the profile loads we deliberately render nothing rather than
  // flash the tip and then hide it — that flicker is more annoying than
  // missing the nudge for one render.
  if (!profile) return null;

  // The flag is additive (Task #79) so older clients/types may not see
  // it on the generated Profile type. Read defensively.
  const dismissed = (profile as { dismissedFreeTextWarning?: boolean })
    .dismissedFreeTextWarning === true;
  if (dismissed) return null;

  const handleDismiss = () => {
    // Optimistic write — flip the cached profile immediately so every
    // mounted DataMinimisationTip on the page disappears in the same
    // render. The server PUT then persists the flag.
    queryClient.setQueryData(
      getGetProfileQueryKey(),
      (prev: unknown) =>
        prev && typeof prev === "object"
          ? { ...prev, dismissedFreeTextWarning: true }
          : prev,
    );
    updateProfile.mutate(
      { data: { dismissedFreeTextWarning: true } as never },
      {
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: getGetProfileQueryKey() });
        },
      },
    );
  };

  return (
    <div
      className={`flex items-start gap-1.5 text-[11px] text-gray-500 leading-snug ${className}`}
    >
      <Info className="h-3 w-3 mt-0.5 shrink-0 text-gray-400" />
      <span className="flex-1">
        {text ??
          "Tip: keep notes brief and avoid sensitive details (diagnoses, addresses, DOBs, parent contact, medical info). Use first names or codes where possible."}
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 -mt-0.5 -mr-1 p-0.5 rounded text-gray-300 hover:text-gray-500 hover:bg-gray-100"
        aria-label="Dismiss tip"
        title="Don't show this tip again"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
